/**
 * AD-021b — enemy pilots going over the side of truly doomed aeroplanes.
 *
 * The request, verbatim from the user (2026-08-27): *"can we get enemies jump
 * out of some truly doomed planes for ambience (no gameplay impact)"*.
 *
 * ## This is a picture and nothing else, and that is what makes it cheap
 *
 * AD-021 §9 costed AI bail-out at **a lane**, and was right to: as *gameplay* it
 * moves kill credit, the squadron's loss column, whether a canopy can be shot
 * and every mission census. The user ruled all of that out in the same sentence
 * he asked for the picture, which takes this out of the engine entirely and puts
 * it in the same class as the crash scorch (`scene-crash-marks.ts`), the ground
 * shadows (`scene-shadows.ts`) and the particles: **a view-side effect that
 * reads sim state and never writes it.**
 *
 * So nothing in this module is on `EncounterState`. `encounterHash` does not
 * move, `engineFingerprint` does not move, `SNAPSHOT_VERSION` does not move,
 * and a `/duel` peer on a build without this file flies the identical
 * encounter. `ambient-jumpers.test.ts` asserts that rather than leaving it to
 * this paragraph: it hashes an encounter, runs a full descent's worth of
 * updates against it, and hashes it again.
 *
 * ## The trigger: burning, committed, and high enough — not merely destroyed
 *
 * "Truly doomed" cannot be `status === "outOfControl"`, and the reason is worth
 * writing down because it looks like it can. `aircraftStatus` returns
 * `outOfControl` for `isWreck(damage, pilot) || fire.burning`, and with a live
 * un-bailed pilot `isWreck` reduces to `wingsFailed` — so that status is
 * *already* "wings off or burning" and testing it again buys nothing. Four
 * further conditions do the work:
 *
 * 1. **He is burning.** The manual's own history chapter supplies the image
 *    this feature exists to draw — *"The sight of a pilot jumping from a flaming
 *    airplane … was all too common"* (`docs/game/manual/02-historical-overview.md`,
 *    quoted at length in `bail-out-design.md` §1). Fire is also the slow death:
 *    a man with a wing folded is pinned in a spin he cannot climb out of, and
 *    `fire.burning` is the case where he has both the seconds and the reason.
 * 2. **He has been going down long enough to have decided.** `fallingSeconds >=
 *    AMBIENT_JUMP_DECISION_SECONDS`, so a jumper never appears on the tick the
 *    rounds land — which would read as the shot throwing him out rather than as
 *    him leaving.
 * 3. **His jump would actually be worth looking at.** Not a minimum altitude —
 *    `jumpWorthDrawing` flies his own jump on AD-021's integrator and asks
 *    whether the canopy is still open `AMBIENT_CANOPY_SECONDS` later. The first
 *    draft of this lane *did* use a constant, and measuring it is what showed a
 *    constant cannot work here; the table is under `AMBIENT_CANOPY_SECONDS`.
 * 4. **He is alive, in his seat, and is not the player.** The player's bail is
 *    his own verb and his own canopy (`EncounterState.parachute`); drawing an
 *    ambience jumper out of his aeroplane would put two men under two canopies
 *    over one cockpit.
 *
 * ## "Some": a hash of the aeroplane, not a draw from any bank
 *
 * `AMBIENT_JUMP_CHANCE` of the aeroplanes that pass all four get a jumper.
 * **The derivation is the view's own and touches no sim RNG.** Drawing from
 * `EncounterState.rng` would advance a bank two lockstep peers have to agree on
 * and would turn a picture into a desync; so this is `fnv1a32(aircraftId,
 * encounterSeed)`, which is the same hash `encounter.ts` already uses to give
 * each aeroplane its own seed, read rather than advanced. It is a pure function
 * of two numbers that were fixed before the sortie started, so the *set* of
 * aeroplanes that produce a jumper is identical on every replay of a tape and
 * on both ends of a duel.
 *
 * A minority on purpose. Most pilots in this war had nothing to jump with, and
 * the ambience is meant to be a thing you occasionally see rather than the
 * standard ending of a kill.
 *
 * **No nation or date gate**, which is AD-021 §9's call made a second time and
 * for its reason: the German Air Service had these from 1918 and the Allies
 * never issued them, but gating on it would make the effect silently absent for
 * most of the fights a career flies, and the encounter does not carry a nation
 * anyway (it has `friendly`/`hostile`, which is player-relative and would be
 * backwards for half the careers). Recorded so the choice is visible rather
 * than accidental. **Taste, declared.**
 *
 * ## The descent is AD-021's, imported
 *
 * `beginJump` and `stepParachute` from `combat/parachute.ts`, unmodified and
 * un-copied. That is deliberate and is the point: the ambience jumper falls at
 * the same 5.5 m/s, blossoms after the same 2.5 s, and drifts on the same
 * `windVelocity` sample the player's canopy does, because it is literally the
 * same integrator. Both are pure functions that return a new state — they read
 * the doomed aeroplane's `SimState` and write nothing — so importing the engine
 * here does not make this module part of the sim.
 *
 * ## Stepped on sim time, at a fixed step
 *
 * Not on the frame clock. Each jumper's age is `encounter.timeS` minus the sim
 * time he left, and the integrator is advanced in whole `AMBIENT_STEP_S` chunks
 * of it — so the number of steps he has taken is `floor(age / AMBIENT_STEP_S)`
 * whatever the frame rate did, and a tape replayed on a slower machine puts him
 * in the same field. (Where the replay can still differ by a metre: he leaves
 * from the aeroplane's pose on the **frame that first observed** the trigger, so
 * a replay that samples a tick later starts him a tick further along the
 * doomed aeroplane's arc. Which aeroplanes jump, and whether they land, do not
 * depend on that.)
 *
 * ## Toggle: ENHANCED EFFECTS, and nothing else
 *
 * `scene-crash-marks.ts`'s argument verbatim in spirit — this is a visual
 * phenomenon the original never drew, which is what that toggle already means.
 * No new setting: the user asked for ambience, and ambience rides with the
 * crash fire and smoke it appears among.
 */

import {
  beginJump,
  fnv1a32,
  PARACHUTE_PULL_SECONDS,
  stepParachute,
  type CombatAircraft,
} from "@red-opus/engine";
import { PARACHUTE_GEOMETRY } from "./canopy-kit.ts";

/**
 * Seconds a doomed pilot spends going down before he is drawn leaving.
 *
 * Invented, and small on purpose: long enough that the jumper is never on the
 * same tick as the burst that caused it, short enough that he is still high
 * when he goes. A burning aeroplane at `wreckSpiralBankRad` loses height fast,
 * and a decision much longer than this puts every jumper below the floor below.
 */
export const AMBIENT_JUMP_DECISION_SECONDS = 2.5;

/**
 * How long the open canopy must be visible for a jump to be worth drawing,
 * seconds.
 *
 * **This is the only taste input on the height question, and there is
 * deliberately no minimum-altitude constant beside it.** The first draft had
 * one — `AMBIENT_JUMP_MIN_AGL_M = 120`, reasoned out on paper — and walking the
 * real integrator says a single number cannot do the job, because the height a
 * jump needs is dominated by how hard the aeroplane was already going down:
 *
 * | wreck's sink rate | height needed for 6 s of open canopy |
 * |---|---|
 * | 5 m/s (a burning glide) | 81 m |
 * | 25 m/s | 132 m |
 * | 55 m/s (a spiral going in) | 208 m |
 *
 * A constant picked anywhere in that spread is wrong at one end: at 120 m a man
 * out of a steep spiral is killed by the fall this feature was supposed to save
 * him from, and at 208 m the gentle cases that would have looked best never
 * happen. So the gate is `jumpWorthDrawing`, which flies **his** jump on
 * AD-021's own integrator and asks the outcome directly — the discipline
 * `bail-out-design.md` §4.3 records after that lane got its own minimum wrong
 * twice on paper, taken one step further: derive the *answer* per case rather
 * than deriving a constant and hoping the cases are alike.
 */
export const AMBIENT_CANOPY_SECONDS = 6;

/**
 * The fraction of qualifying aeroplanes that produce a jumper.
 *
 * A minority, for the reason in the module note. Held as an integer per-mille
 * so the comparison is exact integer arithmetic on both ends of a duel and on
 * every replay — a float threshold is a thing two builds can round differently.
 */
export const AMBIENT_JUMP_PER_MILLE = 200;

/**
 * The most canopies in the sky at once.
 *
 * A budget, not a rule about the war: four rigs is 4 x 4 objects over shared
 * buffers, and a doomed flight of five arriving together should not turn the sky
 * into a laundry line. A qualifying aeroplane that finds the slots full simply
 * does not produce a jumper and is never reconsidered — a man does not climb out
 * thirty seconds late because somebody else landed.
 */
export const MAX_AMBIENT_JUMPERS = 4;

/**
 * The fixed sim-time step the descent is integrated on, seconds.
 *
 * 30 Hz rather than the sim's own rate because a canopy at 5.5 m/s moves 18 cm
 * in a thirtieth of a second and nothing in this picture resolves that; halving
 * the steps halves the only per-frame arithmetic here.
 */
export const AMBIENT_STEP_HZ = 30;

export const AMBIENT_STEP_S = 1 / AMBIENT_STEP_HZ;

/**
 * The pendulum, taken from the player's canopy rather than chosen again — one
 * visual vocabulary, which is the whole reason the silk is a shared kit.
 */
export const AMBIENT_SWAY_RAD = PARACHUTE_GEOMETRY.SWAY_RAD;

/**
 * Is this aeroplane one a man would leave, and is he in a state to?
 *
 * The four cheap conditions, in the order that rejects fastest. The height
 * question is `jumpWorthDrawing`'s and is asked separately because it costs a
 * ground query and a short integration — this one is asked of every aeroplane
 * on every frame and must stay arithmetic.
 */
export function jumperEligible(aircraft: CombatAircraft): boolean {
  // Never the player: his bail is AD-021's verb and his canopy is
  // `EncounterState.parachute`.
  if (aircraft.ai === null) return false;
  if (aircraft.status !== "outOfControl") return false;
  // A dead man does not climb out, and one who has already gone is already out.
  if (!aircraft.sim.pilot.alive || aircraft.sim.pilot.bailedOut) return false;
  // The fire is the reason and the seconds; see the module note.
  if (!aircraft.fire.burning) return false;
  return aircraft.fallingSeconds >= AMBIENT_JUMP_DECISION_SECONDS;
}

/**
 * The number of steps `jumpWorthDrawing` flies before it answers.
 *
 * The canopy blossoms at `PARACHUTE_PULL_SECONDS` and has to be open for
 * `AMBIENT_CANOPY_SECONDS` after that, so the probe is **bounded at 8.5 s of
 * sim time whatever the aeroplane's altitude** — a jump from 4,000 m is not
 * flown to the ground to find out that it works. That bound is what makes a
 * per-case gate affordable on the frame path.
 */
const PROBE_STEPS = Math.ceil((PARACHUTE_PULL_SECONDS + AMBIENT_CANOPY_SECONDS) / AMBIENT_STEP_S);

/**
 * Would this man's jump actually be worth looking at?
 *
 * Flies **his** jump — his aeroplane's height, his aeroplane's velocity, his
 * weather — on AD-021's own integrator for `PROBE_STEPS`, and asks the one
 * question that matters: is the canopy still open at the end of it. A jump that
 * has already reached the ground by then reports `landed` or `dead` and is
 * refused, which is exactly the case a fixed minimum height was there to catch
 * and got wrong (see `AMBIENT_CANOPY_SECONDS`).
 *
 * The ground is taken as flat at the aeroplane's own height, which is the one
 * approximation here and is a deliberate one: the alternative is 255 terrain
 * samples on the frame a man jumps, and over the ~50 m the canopy drifts in
 * 8.5 s the difference does not change the verdict. The **drawn** descent that
 * follows queries the real surface every step.
 */
export function jumpWorthDrawing(sim: CombatAircraft["sim"], groundHeightM: number): boolean {
  let state = beginJump({ pilotId: "probe", sim });
  for (let step = 0; step < PROBE_STEPS; step += 1) {
    state = stepParachute(state, { dt: AMBIENT_STEP_S, groundHeightM });
    if (state.status === "landed" || state.status === "dead") return false;
  }
  return state.status === "open";
}

/**
 * Whether *this* aeroplane is one of the some.
 *
 * `fnv1a32` read, never a bank advanced — see the module note on why the
 * distinction is the whole safety argument.
 */
export function jumperDrawn(aircraftId: string, encounterSeed: number): boolean {
  return fnv1a32(aircraftId, encounterSeed >>> 0) % 1000 < AMBIENT_JUMP_PER_MILLE;
}
